import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react';
import { identiteCourante } from '../auth/identite.js';
import type { DesignId } from './designs.js';

/**
 * Teinte d'un design : choix d'affichage propre à chaque compte, mémorisé dans
 * ce navigateur comme le design, avec une teinte par design. Pour Liquid Glass
 * elle change le fond (et ses taches d'ambiance), pour Halo, Monolithe et
 * Spatial la couleur d'accent des boutons, liens et pastilles. La première
 * teinte de chaque liste est celle par défaut.
 */
export const TEINTES_VERRE = [
  'aurore',
  'craie',
  'perle',
  'glacier',
  'sable',
  'sauge',
  'lilas',
] as const;
export type TeinteVerre = (typeof TEINTES_VERRE)[number];
export const TEINTE_VERRE_DEFAUT: TeinteVerre = 'aurore';

export const TEINTES_PAR_DESIGN = {
  halo: ['lavande', 'ardoise', 'sauge', 'terracotta', 'rose'],
  monolithe: ['champagne', 'graphite', 'cuivre', 'olive'],
  spatial: ['bleu', 'indigo', 'sarcelle', 'ambre', 'graphite'],
  'liquid-glass': TEINTES_VERRE,
} as const;
export type DesignAvecTeintes = keyof typeof TEINTES_PAR_DESIGN;
export type Teinte = (typeof TEINTES_PAR_DESIGN)[DesignAvecTeintes][number];

export function aDesTeintes(design: DesignId): design is DesignAvecTeintes {
  return Object.hasOwn(TEINTES_PAR_DESIGN, design);
}

export function teintesDe(design: DesignAvecTeintes): readonly Teinte[] {
  return TEINTES_PAR_DESIGN[design];
}

export function teinteParDefaut(design: DesignAvecTeintes): Teinte {
  return TEINTES_PAR_DESIGN[design][0];
}

export function estTeinte(design: DesignAvecTeintes, valeur: unknown): valeur is Teinte {
  return (
    typeof valeur === 'string' && (TEINTES_PAR_DESIGN[design] as readonly string[]).includes(valeur)
  );
}

export function estTeinteVerre(valeur: unknown): valeur is TeinteVerre {
  return estTeinte('liquid-glass', valeur);
}

export const TEINTE_STORAGE_PREFIX = 'artosera:teinte:v1:';
/** Liquid Glass garde sa clé d'origine ; les autres designs ont la leur. */
export function teinteStorageKey(
  compteId: string | null,
  design: DesignAvecTeintes = 'liquid-glass',
): string {
  const prefixe =
    design === 'liquid-glass' ? TEINTE_STORAGE_PREFIX : `${TEINTE_STORAGE_PREFIX}${design}:`;
  return compteId === null ? `${prefixe}anonymous` : `${prefixe}user:${compteId}`;
}

// Si le stockage est refusé ou plein, la page courante respecte quand même le choix.
const volatile = new Map<string, Teinte>();
const listeners = new Set<() => void>();
function announce(): void {
  for (const listener of listeners) listener();
}

export function readTeinte(
  compteId: string | null,
  design: DesignAvecTeintes = 'liquid-glass',
): Teinte {
  const key = teinteStorageKey(compteId, design);
  const defaut = teinteParDefaut(design);
  if (volatile.has(key)) return volatile.get(key) ?? defaut;
  try {
    const value = window.localStorage.getItem(key);
    return estTeinte(design, value) ? value : defaut;
  } catch {
    return defaut;
  }
}

export function writeTeinte(
  compteId: string | null,
  value: unknown,
  design: DesignAvecTeintes = 'liquid-glass',
): void {
  if (!estTeinte(design, value)) return;
  const key = teinteStorageKey(compteId, design);
  try {
    window.localStorage.setItem(key, value);
    volatile.delete(key);
  } catch {
    volatile.set(key, value);
  }
  announce();
}

function subscribeTeinte(listener: () => void): () => void {
  listeners.add(listener);
  function onStorage(event: StorageEvent): void {
    if (event.key === null || event.key.startsWith(TEINTE_STORAGE_PREFIX)) {
      if (event.key === null) volatile.clear();
      else volatile.delete(event.key);
      listener();
    }
  }
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Pose la teinte sur <html> ; un design sans teinte n'en garde aucune. */
export function applyTeinte(teinte: Teinte | null): void {
  if (typeof document === 'undefined') return;
  if (teinte === null) delete document.documentElement.dataset.teinte;
  else document.documentElement.dataset.teinte = teinte;
}

/**
 * La teinte du design courant. Omettre le compte lit l'identité authentifiée ;
 * `null` est explicitement anonyme. Un design sans teinte (les sept autres
 * thèmes) renvoie `teinte: null`.
 */
export function useTeinte(design: DesignId, compteId?: string | null) {
  const identite = identiteCourante();
  const account = compteId === undefined ? (identite?.compteId ?? null) : compteId;
  const avecTeintes = aDesTeintes(design);
  const snapshot = useCallback(
    () => (avecTeintes ? readTeinte(account, design) : null),
    [account, avecTeintes, design],
  );
  const teinte = useSyncExternalStore(subscribeTeinte, snapshot, () => null);
  const setTeinte = useCallback(
    (suivante: Teinte) => {
      if (avecTeintes) writeTeinte(account, suivante, design);
    },
    [account, avecTeintes, design],
  );
  useLayoutEffect(() => {
    applyTeinte(teinte);
  }, [teinte, account]);
  return { teinte, setTeinte, teintes: avecTeintes ? teintesDe(design) : [] };
}
